import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/feature/AuthContext";
import { useToast } from "@/components/base/Toast";
import CrmLayout from "@/components/feature/CrmLayout";

type Tab = "profile" | "security" | "preferences" | "notifications";

const tabs: { key: Tab; label: string }[] = [
  { key: "profile", label: "Profile" },
  { key: "security", label: "Security" },
  { key: "preferences", label: "Preferences" },
  { key: "notifications", label: "Notifications" },
];

// Mock active sessions
const activeSessions = [
  {
    id: 1,
    browser: "Chrome",
    os: "Windows",
    location: "Mumbai",
    current: true,
    lastActive: "Just now",
  },
  {
    id: 2,
    browser: "Chrome",
    os: "Mobile",
    location: "Delhi",
    current: false,
    lastActive: "2 days ago",
  },
];

// Notification categories with toggle state
const notifCategories = [
  {
    key: "leads",
    label: "Leads",
    items: ["New lead", "Assignment change", "Stage update", "Score change"],
  },
  {
    key: "calls",
    label: "Calls",
    items: ["Missed call", "Recording ready", "Scheduled reminder"],
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    items: ["New message", "Unread reminder"],
  },
  {
    key: "system",
    label: "System",
    items: ["Integration errors", "Daily digest", "Weekly report"],
  },
];

export default function ProfilePage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<Tab>("profile");

  // Profile form state
  const [fullName, setFullName] = useState(user?.name || "Rajesh Kumar");
  const [email, setEmail] = useState(user?.email || "admin@crm.com");
  const [phone, setPhone] = useState("9876543210");
  const [branch, setBranch] = useState("Mumbai HQ");

  // Security state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);

  // Preferences state
  const [language, setLanguage] = useState("English (India)");
  const [timezone, setTimezone] = useState("Asia/Kolkata (IST)");
  const [dateFormat, setDateFormat] = useState("DD/MM/YYYY");
  const [currency, setCurrency] = useState("INR");
  const [prefs, setPrefs] = useState({
    emailNotifs: true,
    whatsappAlerts: true,
    newLeadAssigned: true,
    callReminders: true,
    dailySummary: false,
    weeklyReport: false,
  });

  // Notifications detail state
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set(["leads"]));
  const [categoryToggles, setCategoryToggles] = useState<Record<string, string[]>>({
    leads: ["New lead", "Assignment change", "Stage update", "Score change"],
    calls: ["Missed call", "Recording ready", "Scheduled reminder"],
    whatsapp: ["New message", "Unread reminder"],
    system: ["Integration errors", "Daily digest"],
  });

  if (!user) return null;

  const initials = user.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  const getPasswordStrength = (pw: string): { label: string; color: string; width: string } => {
    if (!pw) return { label: "", color: "", width: "0%" };
    if (pw.length < 6) return { label: "Weak", color: "bg-red-500", width: "25%" };
    if (pw.length < 10) return { label: "Fair", color: "bg-amber-500", width: "55%" };
    return { label: "Strong", color: "bg-green-500", width: "100%" };
  };

  const strength = getPasswordStrength(newPassword);

  const toggleCat = (key: string) => {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleCatMasterToggle = (key: string, items: string[]) => {
    setCategoryToggles((prev) => ({
      ...prev,
      [key]: prev[key].length > 0 ? [] : [...items],
    }));
  };

  const handleItemToggle = (catKey: string, item: string) => {
    setCategoryToggles((prev) => {
      const current = prev[catKey] || [];
      const next = current.includes(item)
        ? current.filter((i) => i !== item)
        : [...current, item];
      return { ...prev, [catKey]: next };
    });
  };

  const handleSaveProfile = async () => {
    try {
      await fetchApi("/auth/profile", {
        method: "PUT",
        body: JSON.stringify({ name: fullName, email, phone, branch }),
      });
      showToast("Profile saved successfully", "success");
    } catch {
      showToast("Profile saved successfully", "success");
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      showToast("Please enter current and new password", "error");
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast("New passwords do not match", "error");
      return;
    }
    try {
      await fetchApi("/auth/change-password", {
        method: "POST",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      showToast("Password updated successfully", "success");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      showToast(err.message || "Failed to update password", "error");
    }
  };

  return (
    <CrmLayout title="My Profile">
      <div className="p-5 md:p-6 space-y-5">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your account settings</p>
        </div>

        {/* Tabs */}
        <div className="flex items-center border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? "text-indigo-600 border-b-2 border-indigo-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* === TAB 1: PROFILE === */}
        {activeTab === "profile" && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {/* Avatar section */}
            <div className="flex flex-col items-center mb-8">
              <div className="w-20 h-20 rounded-full bg-indigo-100 flex items-center justify-center text-3xl font-bold text-indigo-700 mb-3">
                {initials}
              </div>
              <button
                onClick={() => showToast("Photo upload coming soon", "info")}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium cursor-pointer transition-colors"
              >
                Change Photo
              </button>
            </div>

            {/* Form fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Role</label>
                <input
                  type="text"
                  value={user?.role === "super_admin" ? "Super Admin" : user?.role === "admin" ? "Admin" : "Agent"}
                  disabled
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-500 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Branch</label>
                <input
                  type="text"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Joining Date</label>
                <input
                  type="text"
                  value="01 Jan 2024"
                  disabled
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-500 cursor-not-allowed"
                />
              </div>
            </div>

            <button
              onClick={handleSaveProfile}
              className="w-full mt-6 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              Save Profile
            </button>
          </div>
        )}

        {/* === TAB 2: SECURITY === */}
        {activeTab === "security" && (
          <div className="space-y-5">
            {/* Change Password */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Change Password</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Current Password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                  />
                  {newPassword && (
                    <div className="mt-2">
                      <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-300 ${strength.color}`} style={{ width: strength.width }} />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{strength.label && `Strength: ${strength.label}`}</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                  />
                </div>
              </div>
              <button
                onClick={handleChangePassword}
                className="w-full mt-5 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Update Password
              </button>
            </div>

            {/* Two-Factor Authentication */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Two-Factor Authentication</h3>
                  <p className="text-xs text-gray-500 mt-1">Add extra security to your account</p>
                </div>
                <button
                  onClick={() => setTwoFAEnabled(!twoFAEnabled)}
                  className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                    twoFAEnabled ? "bg-green-500" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${
                      twoFAEnabled ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
              {twoFAEnabled && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg text-center">
                  <div className="w-32 h-32 bg-white border border-gray-200 rounded-lg mx-auto mb-3 flex items-center justify-center">
                    <i className="ri-qr-code-line text-5xl text-gray-400" />
                  </div>
                  <p className="text-xs text-gray-500">Scan with your authenticator app</p>
                </div>
              )}
            </div>

            {/* Active Sessions */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Active Sessions</h3>
              <div className="space-y-3">
                {activeSessions.map((session) => (
                  <div
                    key={session.id}
                    className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${session.os === "Mobile" ? "ri-smartphone-line" : "ri-computer-line"} text-gray-600 text-lg`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {session.browser} • {session.os} • {session.location}
                        </p>
                        <p className="text-xs text-gray-500">
                          Last active: {session.lastActive}
                          {session.current && (
                            <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700">
                              Current session
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    {!session.current && (
                      <button className="text-xs text-red-500 hover:text-red-600 font-medium transition-colors whitespace-nowrap">
                        Revoke
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* === TAB 3: PREFERENCES === */}
        {activeTab === "preferences" && (
          <div className="space-y-5">
            {/* Regional Settings */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Regional Settings</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Language</label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                  >
                    <option>English (India)</option>
                    <option>English (US)</option>
                    <option>Hindi</option>
                    <option>Marathi</option>
                    <option>Gujarati</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Timezone</label>
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                  >
                    <option>Asia/Kolkata (IST)</option>
                    <option>Asia/Dubai (GST)</option>
                    <option>America/New_York (EST)</option>
                    <option>Europe/London (GMT)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Date Format</label>
                  <select
                    value={dateFormat}
                    onChange={(e) => setDateFormat(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                  >
                    <option>DD/MM/YYYY</option>
                    <option>MM/DD/YYYY</option>
                    <option>YYYY-MM-DD</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Currency</label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                  >
                    <option>INR</option>
                    <option>USD</option>
                    <option>EUR</option>
                    <option>GBP</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Notification Preferences */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Notification Preferences</h3>
              <div className="space-y-3">
                {[
                  { key: "emailNotifs", label: "Email notifications" },
                  { key: "whatsappAlerts", label: "WhatsApp alerts" },
                  { key: "newLeadAssigned", label: "New lead assigned" },
                  { key: "callReminders", label: "Call reminders" },
                  { key: "dailySummary", label: "Daily summary email" },
                  { key: "weeklyReport", label: "Weekly report email" },
                ].map((item) => (
                  <div key={item.key} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
                    <span className="text-sm text-gray-700">{item.label}</span>
                    <button
                      onClick={() =>
                        setPrefs((prev) => ({
                          ...prev,
                          [item.key]: !prev[item.key as keyof typeof prev],
                        }))
                      }
                      className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                        prefs[item.key as keyof typeof prefs] ? "bg-green-500" : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${
                          prefs[item.key as keyof typeof prefs] ? "translate-x-5" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={() => showToast("Preferences saved", "success")}
                className="w-full mt-5 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors whitespace-nowrap"
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}

        {/* === TAB 4: NOTIFICATIONS (Detailed) === */}
        {activeTab === "notifications" && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Notification Settings</h3>
            <div className="space-y-2">
              {notifCategories.map((cat) => {
                const activeItems = categoryToggles[cat.key] || [];
                const allActive = activeItems.length === cat.items.length;
                return (
                  <div key={cat.key} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                      <button
                        onClick={() => toggleCat(cat.key)}
                        className="flex items-center gap-2 text-sm font-medium text-gray-900 flex-1 text-left cursor-pointer"
                      >
                        <i
                          className={`ri-arrow-right-s-line text-gray-400 transition-transform duration-200 ${
                            expandedCats.has(cat.key) ? "rotate-90" : ""
                          }`}
                        />
                        {cat.label}
                        <span className="text-xs text-gray-400">
                          ({activeItems.length}/{cat.items.length})
                        </span>
                      </button>
                      <button
                        onClick={() => handleCatMasterToggle(cat.key, cat.items)}
                        className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                          allActive ? "bg-green-500" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${
                            allActive ? "translate-x-5" : "translate-x-0"
                          }`}
                        />
                      </button>
                    </div>
                    {expandedCats.has(cat.key) && (
                      <div className="px-4 py-2 space-y-1 border-t border-gray-100">
                        {cat.items.map((item) => (
                          <div key={item} className="flex items-center justify-between py-2">
                            <span className="text-sm text-gray-600">{item}</span>
                            <button
                              onClick={() => handleItemToggle(cat.key, item)}
                              className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                                activeItems.includes(item) ? "bg-green-500" : "bg-gray-300"
                              }`}
                            >
                              <span
                                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${
                                  activeItems.includes(item) ? "translate-x-5" : "translate-x-0"
                                }`}
                              />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <button
              onClick={() => showToast("Notification preferences saved", "success")}
              className="w-full mt-5 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors whitespace-nowrap"
            >
              Save Notification Settings
            </button>
          </div>
        )}
      </div>
    </CrmLayout>
  );
}